import React, { useEffect, useState } from "react";
import {
  Table,
  Button,
  Input,
  Modal,
  Form,
  message,
  ConfigProvider,
  Popconfirm,
  Space,
  Select,
} from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined } from "@ant-design/icons";
import "../adminShared/adminPage.css";
import { routes } from "../../api/routes";
import { callApi } from "../../api/apiCaller";
import Loader from "../../components/loader/loader";
import { listFromResponse, isMutationSuccess } from "../../helper/helper";
import dayjs from "dayjs";

const VehicleModels = () => {
  const [allRows, setAllRows] = useState([]);
  const [makes, setMakes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [form] = Form.useForm();

  const loadMakes = () => {
    callApi(
      "GET",
      routes.getAllVehicleMakes,
      null,
      setLoading,
      (res) => {
        if (res.status === 200) setMakes(listFromResponse(res));
      },
      () => {}
    );
  };

  const loadModels = () => {
    callApi(
      "GET",
      routes.getAllVehicleModels,
      null,
      setLoading,
      (res) => {
        if (res.status === 200) {
          const data = listFromResponse(res);
          setAllRows(data);
        }
      },
      () => message.error("Failed to load vehicle models")
    );
  };

  useEffect(() => {
    loadMakes();
    loadModels();
  }, []);

  const getMakeLabel = (row) => {
    const m = row.vehicleMake;
    if (typeof m === "object" && m?.title) return m.title;
    const id = typeof m === "string" ? m : m?._id;
    const found = makes.find((x) => x._id === id);
    return found?.title || id || "—";
  };

  const filtered = searchText
    ? allRows.filter((r) => (r.title || "").toLowerCase().includes(searchText.toLowerCase()))
    : allRows;

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    setModalOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    const makeId =
      typeof row.vehicleMake === "object" ? row.vehicleMake?._id : row.vehicleMake;
    form.setFieldsValue({ title: row.title, vehicleMake: makeId });
    setModalOpen(true);
  };

  const submit = (values) => {
    setModalOpen(false);
    const current = editing;
    setEditing(null);
    form.resetFields();

    if (current) {
      callApi(
        "PATCH",
        `${routes.updateVehicleModel}/${current._id}`,
        { title: values.title, vehicleMake: values.vehicleMake },
        setLoading,
        (res) => {
          if (res.status === 200) {
            message.success("Model updated");
            loadModels();
          }
        },
        () => message.error("Failed to update model")
      );
    } else {
      callApi(
        "POST",
        routes.createVehicleModel,
        { title: values.title, vehicleMake: values.vehicleMake },
        setLoading,
        (res) => {
          if (res.status === 200 || res.status === 201) {
            message.success("Model created");
            loadModels();
          }
        },
        () => message.error("Failed to create model")
      );
    }
  };

  const remove = (id) => {
    callApi(
      "DELETE",
      `${routes.deleteVehicleModel}/${id}`,
      null,
      setLoading,
      (res) => {
        if (isMutationSuccess(res)) {
          message.success("Model deleted");
          loadModels();
        }
      },
      () => message.error("Failed to delete model")
    );
  };

  const columns = [
    { title: "No", key: "i", width: 60, render: (_, __, i) => i + 1 },
    { title: "Title", dataIndex: "title", key: "title" },
    {
      title: "Make",
      key: "make",
      render: (_, row) => getMakeLabel(row),
    },
    {
      title: "Created",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 140,
      render: (d) => (d ? dayjs(d).format("DD MMM YYYY") : "—"),
    },
    {
      title: "Action",
      key: "a",
      width: 160,
      render: (_, row) => (
        <Space>
          <Button type="primary" size="small" icon={<EditOutlined />} onClick={() => openEdit(row)} />
          <Popconfirm title="Delete this model?" onConfirm={() => remove(row._id)}>
            <Button danger size="small" icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <ConfigProvider theme={{ token: { colorPrimary: "#555BDF" } }}>
      <div className="admin-entity-page">
        <Loader loading={loading} />
        <div className="admin-entity-header">
          <h1 className="admin-entity-title">Vehicle Models</h1>
        </div>
        <div className="admin-entity-card">
          <div className="admin-entity-actions">
            <h2 style={{ margin: 0, fontSize: 16 }}>Catalog</h2>
            <Space>
              <Input
                placeholder="Search…"
                prefix={<SearchOutlined />}
                allowClear
                style={{ width: 260 }}
                onChange={(e) => setSearchText(e.target.value || "")}
              />
              <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
                Add model
              </Button>
            </Space>
          </div>
          <Table
            className="admin-entity-table"
            rowKey="_id"
            columns={columns}
            dataSource={filtered}
            scroll={{ x: true }}
          />
        </div>
        <Modal
          title={editing ? "Edit model" : "New model"}
          open={modalOpen}
          onCancel={() => {
            setModalOpen(false);
            setEditing(null);
            form.resetFields();
          }}
          onOk={() => form.submit()}
          destroyOnClose
        >
          <Form form={form} layout="vertical" onFinish={submit}>
            <Form.Item name="vehicleMake" label="Make" rules={[{ required: true, message: "Required" }]}>
              <Select
                placeholder="Select make"
                options={makes.map((m) => ({ value: m._id, label: m.title }))}
                showSearch
                optionFilterProp="label"
              />
            </Form.Item>
            <Form.Item name="title" label="Title" rules={[{ required: true, message: "Required" }]}>
              <Input placeholder="e.g. A3" />
            </Form.Item>
          </Form>
        </Modal>
      </div>
    </ConfigProvider>
  );
};

export default VehicleModels;
